/**
 * Implements a chart reviews route interceptor as a behavior.
 *
 * It must attached to the main layout.
 */

import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import { createModal } from "../utils";

import NewProjectView from "../views/chart-reviews/projects/add";
import EditProjectView from "../views/chart-reviews/projects/edit";
import CreateChartView from "../views/chart-reviews/create";
import EditChartView from "../views/chart-reviews/edit";
import BulkEditChartView from "../views/chart-reviews/bulk-edit";
import BulkAssignChartView from "../views/chart-reviews/bulk-assign";
import BulkImportChartView from "../views/chart-reviews/bulk-import";
import EditDRGAddsView from "../views/chart-reviews/drg-adds-edit";
import EditDRGDeletesView from "../views/chart-reviews/drg-deletes-edit";
import MemberSearchView from "../views/search/member";
import ProviderSearchView from "../views/search/provider";
import EditDRGAddsModel from "../models/chart-reviews/drg-adds-edit-form";
import EditDRGDeletesModel from "../models/chart-reviews/drg-deletes-edit-form";
import ProjectModel from "../models/chart-reviews/project";
import ChartModel from "../models/chart-reviews/chart-form";
import ChartBulkEditModel from "../models/chart-reviews/chart-bulk-edit-form";
import ModalView from "../views/drilldown";
import DatatableLinkModel from "../models/datatable-link";

import environment from "../common/environment";

const globalChannel = Radio.channel("global");
const reportChannel = Radio.channel("report");

const BULK_EDIT_SUCCESS = "Your bulk update was successful.";
const BULK_EDIT_PARTIAL_SUCCESS =
  "Portions of your bulk update did not complete successfully.";
const BULK_EDIT_FAILURE = "The bulk update failed.";

class ChartReviewBehaviors extends Marionette.Behavior {
  initialize() {
    this.listenTo(
      globalChannel,
      "chart-reviews.project:create",
      this.onCreateProject
    );
    this.listenTo(
      globalChannel,
      "chart-reviews.chart:create",
      this.onCreateChart
    );
    this.listenTo(globalChannel, "chart-reviews-*", this.onEditChart);
    this.listenTo(
      globalChannel,
      "chart-reviews-projects-*",
      this.onEditProject
    );
    this.listenTo(
      globalChannel,
      "chart-reviews-created",
      this.onEditChartAfterCreation
    );
    this.listenTo(
      globalChannel,
      "chart-reviews-bulk-edit",
      this.onBulkEditCharts
    );
    this.listenTo(
      globalChannel,
      "chart-reviews-bulk-assign",
      this.onBulkAssignCharts
    );
    this.listenTo(
      globalChannel,
      "chart-reviews-bulk-import",
      this.onBulkImportCharts
    );
    this.listenTo(
      globalChannel,
      "chart-reviews.member:search",
      this.onMemberSearch
    );
    this.listenTo(
      globalChannel,
      "chart-reviews.provider:search",
      this.onProviderSearch
    );

    // BugzId 86676
    this.listenTo(
      globalChannel,
      "chart-reviews-*-diagnosis-review-groups-adds-*",
      this.onEditDRGAdds
    );
    this.listenTo(
      globalChannel,
      "chart-reviews-diagnosis-review-groups-deletes-*",
      this.onEditDRGDeletes
    );

    this.listenTo(
      reportChannel,
      "chart-reviews.project:create",
      this.onCreateProject
    );
    this.listenTo(
      reportChannel,
      "chart-reviews.chart:create",
      this.onCreateChart
    );
    this.listenTo(reportChannel, "chart-reviews-*", this.onEditChart);
    this.listenTo(
      reportChannel,
      "chart-reviews-projects-*",
      this.onEditProject
    );
    this.listenTo(
      reportChannel,
      "chart-reviews-created",
      this.onEditChartAfterCreation
    );
    this.listenTo(
      reportChannel,
      "chart-reviews-bulk-edit",
      this.onBulkEditCharts
    );
    this.listenTo(
      reportChannel,
      "chart-reviews-bulk-assign",
      this.onBulkAssignCharts
    );
    this.listenTo(
      reportChannel,
      "chart-reviews-bulk-import",
      this.onBulkImportCharts
    );
    this.listenTo(
      reportChannel,
      "chart-reviews.member:search",
      this.onMemberSearch
    );
    this.listenTo(
      reportChannel,
      "chart-reviews.provider:search",
      this.onProviderSearch
    );

    this.listenTo(
      reportChannel,
      "chart-reviews:drg-adds:bulk-update:filtered",
      this.onAddsBulkUpdate
    );
    this.listenTo(
      reportChannel,
      "chart-reviews:drg-adds:bulk-update:invalidate-filtered",
      this.onAddsBulkUpdate
    );
    this.listenTo(
      reportChannel,
      "chart-reviews:drg-adds:bulk-update:selected",
      this.onAddsBulkUpdate
    );
    this.listenTo(
      reportChannel,
      "chart-reviews:drg-adds:bulk-update:invalidate-selected",
      this.onAddsBulkUpdate
    );
    this.listenTo(
      reportChannel,
      "chart-reviews:drg-deletes:bulk-update:filtered",
      this.onDeletesBulkUpdate
    );
    this.listenTo(
      reportChannel,
      "chart-reviews:drg-deletes:bulk-update:selected",
      this.onDeletesBulkUpdate
    );
    this.listenTo(
      reportChannel,
      "chart-reviews:pending-deletes:bulk-update:filtered",
      this.onPendingDeletesBulkUpdate
    );
    this.listenTo(
      reportChannel,
      "chart-reviews:pending-deletes:bulk-update:invalidate-filtered",
      this.onPendingDeletesBulkUpdate
    );
    this.listenTo(
      reportChannel,
      "chart-reviews:pending-deletes:bulk-update:selected",
      this.onPendingDeletesBulkUpdate
    );
    this.listenTo(
      reportChannel,
      "chart-reviews:pending-deletes:bulk-update:invalidate-selected",
      this.onPendingDeletesBulkUpdate
    );
  }

  onCreateProject() {
    const View = NewProjectView.extend({
      el: createModal(),
    });

    this._createView(View, {}, ProjectModel);
  }

  onEditProject(options) {
    // convert model to JSON
    if (options.toJSON) {
      options = options.toJSON();
    }

    const View = EditProjectView.extend({
      el: createModal(),
    });

    // define view options
    const viewOptions = {
      data: options.data,
    };

    if (options.returnRoute) {
      viewOptions.returnRoute = options.returnRoute;
      viewOptions.returnRouteRefresh = true;
    }

    this._createView(View, viewOptions, ProjectModel, options.data);
  }

  onCreateChart() {
    const View = CreateChartView.extend({
      el: createModal({
        large: true,
        tagAsGenerated: false,
      }),
    });

    const viewOptions = {
      returnRoute: "chart-reviews.charts",
      returnRouteRefresh: true,
    };

    this._createView(View, viewOptions, ChartModel);
  }

  onEditChart(linkModel) {
    // pass return route to since chart edit are linked from other reports
    const viewOptions = {
      returnRoute: linkModel.getReturnRoute(),
      returnRouteRefresh: linkModel.getReturnRouteRefresh(),
    };

    const modelOptions = linkModel.getData();

    this._createView(EditChartView, viewOptions, ChartModel, modelOptions);
  }

  onEditChartAfterCreation(modelOptions) {
    // pass return route to since chart edit are linked from other reports
    const viewOptions = {
      returnRoute: "chart-reviews.charts",
      returnRouteRefresh: true,
    };

    this._createView(EditChartView, viewOptions, ChartModel, modelOptions);
  }

  onBulkEditCharts(options) {
    const View = BulkEditChartView.extend({
      el: createModal({
        tagAsGenerated: false,
      }),
    });

    // pass return route to since chart edit are linked from other reports
    const defaultViewOptions = {
      returnRoute: "chart-reviews.charts",
      returnRouteRefresh: true,
    };

    // merge default and user provided options
    const viewOptions = Object.assign({}, defaultViewOptions, {
      payload: options.payload,
      chartCount: options.chartCount,
    });

    this._createView(View, viewOptions, ChartBulkEditModel);
  }

  onBulkAssignCharts(options) {
    const View = BulkAssignChartView.extend({
      el: createModal({
        tagAsGenerated: false,
        scrollable: true,
      }),
    });

    // pass return route to since chart edit are linked from other reports
    const defaultViewOptions = {
      returnRoute: "chart-reviews.charts",
      returnRouteRefresh: true,
    };

    // merge default and user provided options
    const viewOptions = Object.assign({}, defaultViewOptions, {
      payload: options.payload,
      chartCount: options.chartCount,
    });

    this._createView(View, viewOptions, ChartBulkEditModel);
  }

  onBulkImportCharts() {
    const View = BulkImportChartView.extend({
      el: createModal(),
    });

    this._createView(View);
  }

  onMemberSearch() {
    const View = MemberSearchView.extend({
      el: createModal({
        large: true,
      }),
    });

    this._createView(View);
  }

  onProviderSearch() {
    const View = ProviderSearchView.extend({
      el: createModal({
        large: true,
      }),
    });

    this._createView(View);
  }

  onEditDRGAdds(options) {
    // convert model to JSON
    if (options.toJSON) {
      options = options.toJSON();
    }

    // define model
    const apiName = "chart-reviews:drg-adds";
    const urlRoot = environment.getEndpoint(apiName, {
      chartId: options.data.id,
    });
    const Model = EditDRGAddsModel.extend({ urlRoot });

    // define view and model options
    const viewOptions = {
      data: options.data,
    };

    const modelOptions = {
      id: options.data.diagReviewGroupId,
    };

    if (options.returnRoute) {
      viewOptions.returnRoute = options.returnRoute;
      viewOptions.returnRouteRefresh = true;
    }

    this._createView(EditDRGAddsView, viewOptions, Model, modelOptions);
  }

  onEditDRGDeletes(options) {
    // convert model to JSON
    if (options.toJSON) {
      options = options.toJSON();
    }

    // define model
    const apiName = "chart-reviews:drg-deletes";
    const urlRoot = environment.getEndpoint(apiName);
    const Model = EditDRGDeletesModel.extend({ urlRoot });

    // define view and model options
    const viewOptions = {
      data: options.data,
    };

    const modelOptions = {
      id: options.data.diagReviewGroupId,
    };

    if (options.returnRoute) {
      viewOptions.returnRoute = options.returnRoute;
      viewOptions.returnRouteRefresh = true;
    }

    this._createView(EditDRGDeletesView, viewOptions, Model, modelOptions);
  }

  onAddsBulkUpdate(data, dtAPI) {
    // extract params needed for exception handling
    const bulkExceptionParams = {
      projectId: data.projectId || null,
      returnRoute: data.returnRoute || null,
      returnRouteRefresh: data.returnRoute ? true : false,
    };

    // BugzId 83775
    // enable processing mode on current table
    dtAPI.processing(true);

    $.ajax({
      url: data.endpoint,
      method: "POST",
      contentType: "application/json",
      processData: false,
      data: JSON.stringify(data.payload),
      success: (data, textStatus, jqXHR) => {
        // console.group("Bulk Update");
        // console.info({ data, textStatus, jqXHR });
        switch (jqXHR.status) {
          case 204:
            // display success alert
            this._updateStatus("success", BULK_EDIT_SUCCESS);

            // refresh datatable result
            dtAPI.ajax.reload();

            break;
          case 200:
            // display warning alert
            this._updateStatus("danger", BULK_EDIT_PARTIAL_SUCCESS);

            // load bulk exception view
            const config = Object.assign(bulkExceptionParams, data);
            // console.log({ config });
            this._loadBulkExceptionView("drg-adds", config);

            break;
        }
        // console.groupEnd();
      },
      error: () => {
        // disable processing mode on current table
        dtAPI.processing(false);

        // display error alert
        this._updateStatus("danger", BULK_EDIT_FAILURE);
      },
    });
  }

  onDeletesBulkUpdate(data, dtAPI) {
    // extract params needed for exception handling
    const bulkExceptionParams = {
      returnRoute: data.returnRoute || null,
      returnRouteRefresh: data.returnRoute ? true : false,
    };

    // BugzId 83775
    // enable processing mode on current table
    dtAPI.processing(true);

    $.ajax({
      url: data.endpoint,
      method: "POST",
      contentType: "application/json",
      processData: false,
      data: JSON.stringify(data.payload),
      success: (data, textStatus, jqXHR) => {
        // console.group("Bulk Update");
        // console.info({ data, textStatus, jqXHR });
        switch (jqXHR.status) {
          case 204:
            // display success alert
            this._updateStatus("success", BULK_EDIT_SUCCESS);

            // refresh datatable result
            dtAPI.ajax.reload();

            break;
          case 200:
            // display warning alert
            this._updateStatus("danger", BULK_EDIT_PARTIAL_SUCCESS);

            // load bulk exception view
            const config = Object.assign(bulkExceptionParams, data);
            // console.log({ data });
            this._loadBulkExceptionView("drg-deletes", config);

            break;
        }
        // console.groupEnd();
      },
      error: () => {
        // disable processing mode on current table
        dtAPI.processing(false);

        // display error alert
        this._updateStatus("danger", BULK_EDIT_FAILURE);
      },
    });
  }

  onPendingDeletesBulkUpdate(data, dtAPI) {
    // extract params needed for exception handling
    const bulkExceptionParams = {
      returnRoute: data.returnRoute || null,
      returnRouteRefresh: data.returnRoute ? true : false,
    };

    // BugzId 83775
    // enable processing mode on current table
    dtAPI.processing(true);

    $.ajax({
      url: data.endpoint,
      method: "POST",
      contentType: "application/json",
      processData: false,
      data: JSON.stringify(data.payload),
      success: (data, textStatus, jqXHR) => {
        // console.group("Bulk Update");
        // console.info({ data, textStatus, jqXHR });
        switch (jqXHR.status) {
          case 204:
            // display success alert
            this._updateStatus("success", BULK_EDIT_SUCCESS);

            // refresh datatable result
            dtAPI.ajax.reload();

            break;
          case 200:
            // display warning alert
            this._updateStatus("danger", BULK_EDIT_PARTIAL_SUCCESS);

            // load bulk exception view
            const config = Object.assign(bulkExceptionParams, data);
            // console.log({ data });
            this._loadBulkExceptionView("pending-deletes", config);

            break;
        }
        // console.groupEnd();
      },
      error: () => {
        // disable processing mode on current table
        dtAPI.processing(false);

        // display error alert
        this._updateStatus("danger", BULK_EDIT_FAILURE);
      },
    });
  }

  _loadBulkExceptionView(updateType, config) {
    const viewOptions = {
      returnRoute: config.returnRoute || null,
      returnRouteRefresh: !!(config.returnRoute || null),
    };

    const modelOptions = {
      // modal type is not passed in; will default to "drilldown"
      endpoint: environment.getEndpointPath(
        `chart-reviews:${updateType}:bulk-update:errors`,
        config
      ),
      data: config.params,
    };

    this._createModalView(
      ModalView,
      viewOptions,
      DatatableLinkModel,
      modelOptions
    );
  }

  // instantiates the passed in view with its options and model
  _createView(View, viewOptions = {}, Model, modelOptions = {}) {
    const options = Object.assign({}, viewOptions, {
      model: Model ? new Model(modelOptions) : null,
    });

    // instantiate view
    new View(options);
  }

  // instantiates the passed in view on a generated modal with its options and model
  _createModalView(View, viewOptions = {}, Model, modelOptions = {}) {
    // create new modal and apply returned selector to view options
    Object.assign(viewOptions, {
      el: createModal(),
    });

    this._createView(View, viewOptions, Model, modelOptions);
  }

  _updateStatus(type, message) {
    globalChannel.trigger("status:show", {
      type,
      message,
      autoDismiss: "danger" === type ? false : true,
    });
  }
}

export { ChartReviewBehaviors as default };
